import { useState, useRef, useMemo, useLayoutEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AppWindow, ChevronDown, Gift, GraduationCap, Heart, LayoutGrid, LogOut, Menu, MoreHorizontal, ShoppingCart, User } from "lucide-react";
import Logo from "@/components/Logo";
import SmartSearch from "@/components/SmartSearch";
import { DynamicIcon } from "@/lib/icons";
import { useCategories } from "@/hooks/useCatalog";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { useSiteStatus } from "@/context/SiteStatusContext";
import { CURRENCY_CONFIG } from "@/lib/format";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const EXTRA_LINKS = [
  { label: "Boutiques", to: "/boutiques" },
  { label: "Devenir revendeur", to: "/devenir-revendeur" },
  { label: "Promotions", to: "/promotions", hot: true },
  { label: "Support", to: "/support" },
];

const NAV_LABELS = {
  "jeux-cartes-cadeaux": "Jeux",
  "deblocage-services": "Déblocage & Services",
};

// Catégories regroupées sous l'onglet « Logiciels »
const SOFTWARE_SLUGS = ["windows", "microsoft-office", "antivirus", "adobe", "outils"];

export function DesktopHeader() {
  const { data: categories = [] } = useCategories();
  const { cartCount, favorites, currency, setCurrency } = useStore();
  const { user, logout } = useAuth();
  const { resellers } = useSiteStatus();
  const resellersOn = resellers?.enabled !== false;
  const [megaOpen, setMegaOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const currentCat = new URLSearchParams(location.search).get("categorie");
  const isItemActive = (item) =>
    item.to.includes("?categorie=")
      ? location.pathname === "/boutique" && currentCat === item.key
      : location.pathname === item.to;

  const items = useMemo(() => {
    const softwareCats = [];
    const otherCats = [];
    categories.forEach((cat) => {
      const entry = {
        key: cat.slug,
        label: NAV_LABELS[cat.slug] || cat.name,
        to: `/boutique?categorie=${cat.slug}`,
        testid: `nav-category-${cat.slug}`,
      };
      (SOFTWARE_SLUGS.includes(cat.slug) ? softwareCats : otherCats).push(entry);
    });
    const list = [
      { key: "formations", label: "Formations", to: "/formations", Icon: GraduationCap, testid: "nav-link-formations" },
      { key: "offres", label: "Offres", to: "/offres", Icon: Gift, testid: "nav-link-offres" },
    ];
    if (softwareCats.length) {
      list.push({ key: "logiciels", label: "Logiciels", Icon: AppWindow, testid: "nav-group-logiciels", type: "group", children: softwareCats });
    }
    list.push(
      ...otherCats,
      ...EXTRA_LINKS
        .filter((l) => resellersOn || !["/boutiques", "/devenir-revendeur"].includes(l.to))
        .map((l) => ({ key: l.to, label: l.label, to: l.to, hot: l.hot, testid: `nav-link-${l.to.replace("/", "")}` })),
    );
    return list;
  }, [categories, resellersOn]);

  const navContainerRef = useRef(null);
  const navMeasureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  useLayoutEffect(() => {
    const container = navContainerRef.current;
    const measure = navMeasureRef.current;
    if (!container || !measure) return;
    const MORE_W = 104;
    const compute = () => {
      const available = container.offsetWidth;
      const widths = Array.from(measure.children).map((c) => c.getBoundingClientRect().width + 2);
      const total = widths.reduce((a, b) => a + b, 0);
      if (total <= available) {
        setVisibleCount(items.length);
        return;
      }
      const usable = available - MORE_W;
      let used = 0;
      let count = 0;
      for (const w of widths) {
        if (used + w <= usable) {
          used += w;
          count += 1;
        } else break;
      }
      setVisibleCount(Math.max(count, 1));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    return () => ro.disconnect();
  }, [items]);

  const renderNavItem = (item, forMeasure = false) => {
    if (item.type === "group") {
      const groupBtn = (extra) => (
        <button
          {...extra}
          className="flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-2 text-[13px] font-semibold text-white/90 transition-colors duration-200 hover:bg-white/15 hover:text-white"
        >
          {item.Icon && <item.Icon className="h-4 w-4" />}
          {item.label}
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      );
      if (forMeasure) return <span key={item.key}>{groupBtn({ tabIndex: -1 })}</span>;
      return (
        <DropdownMenu key={item.key}>
          <DropdownMenuTrigger asChild>{groupBtn({ "data-testid": item.testid })}</DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {item.children.map((c) => (
              <DropdownMenuItem asChild key={c.key}>
                <Link to={c.to} data-testid={`nav-group-item-${c.key}`} className="cursor-pointer text-sm font-semibold">
                  {c.label}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }
    return (
      <Link
        key={item.key}
        to={item.to}
        tabIndex={forMeasure ? -1 : undefined}
        data-testid={forMeasure ? undefined : item.testid}
        className={`flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-2 text-[13px] font-semibold transition-colors duration-200 ${
          isItemActive(item) ? "bg-white text-brand" : "text-white/90 hover:bg-white/15 hover:text-white"
        }`}
      >
        {item.Icon && <item.Icon className="h-4 w-4" />}
        {item.label}
        {item.hot && (
          <span className="rounded bg-rose-500 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-white">
            Hot
          </span>
        )}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-50 hidden w-full flex-col bg-white shadow-[0_1px_0_0_#E2E8F0] lg:flex">
      <div className="container-it flex h-[76px] items-center gap-8">
        <Logo />

        <div className="flex-1">
          <SmartSearch />
        </div>

        <nav className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="currency-switcher"
                className="mr-1 flex items-center gap-1 rounded-full border border-line px-3 py-2 text-xs font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
              >
                {currency}
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {Object.entries(CURRENCY_CONFIG).map(([code, config]) => (
                <DropdownMenuItem
                  key={code}
                  data-testid={`currency-option-${code}`}
                  onClick={() => setCurrency(code)}
                  className="cursor-pointer text-sm"
                >
                  <span className="font-semibold">{code}</span>
                  <span className="ml-2 text-slate-500">{config.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="account-menu-btn"
                className="group flex items-center gap-2 rounded-xl px-3 py-2 transition-colors duration-200 hover:bg-slate-50"
              >
                <User className="h-[22px] w-[22px] text-ink" strokeWidth={1.8} />
                <span className="hidden text-left text-[13px] font-semibold leading-tight text-ink xl:block">
                  {user ? user.name?.split(" ")[0] : "Mon compte"}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {user ? (
                <>
                  <div className="px-2 py-1.5">
                    <p className="truncate text-sm font-semibold text-ink">{user.name}</p>
                    <p className="truncate text-xs text-slate-500">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/compte" data-testid="menu-my-account">Tableau de bord</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/compte/licences" data-testid="menu-my-licenses">Mes licences</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/compte/commandes" data-testid="menu-my-orders">Mes commandes</Link>
                  </DropdownMenuItem>
                  {user.role === "vendor" && (
                    <DropdownMenuItem asChild>
                      <Link to="/vendeur/dashboard" data-testid="menu-vendor" className="font-semibold text-brand">
                        Espace vendeur
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" data-testid="menu-admin" className="font-semibold text-brand">
                        Administration
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="menu-logout"
                    onClick={async () => {
                      await logout();
                      navigate("/");
                    }}
                    className="cursor-pointer text-rose-600"
                  >
                    <LogOut className="mr-2 h-4 w-4" /> Déconnexion
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/connexion" data-testid="menu-login">Se connecter</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/inscription" data-testid="menu-register">Créer un compte</Link>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to="/favoris"
            data-testid="header-favorites-link"
            className="group relative flex items-center gap-2 rounded-xl px-3 py-2 transition-colors duration-200 hover:bg-slate-50"
          >
            <span className="relative">
              <Heart className="h-[22px] w-[22px] text-ink" strokeWidth={1.8} />
              {favorites.length > 0 && (
                <span className="absolute -right-2 -top-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-white">
                  {favorites.length}
                </span>
              )}
            </span>
            <span className="hidden text-[13px] font-semibold text-ink xl:block">Favoris</span>
          </Link>

          <Link
            to="/panier"
            data-testid="header-cart-link"
            className="group relative flex items-center gap-2 rounded-xl px-3 py-2 transition-colors duration-200 hover:bg-slate-50"
          >
            <span className="relative">
              <ShoppingCart className="h-[22px] w-[22px] text-ink" strokeWidth={1.8} />
              {cartCount > 0 && (
                <span
                  data-testid="cart-badge"
                  className="absolute -right-2 -top-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-white"
                >
                  {cartCount}
                </span>
              )}
            </span>
            <span className="hidden text-[13px] font-semibold text-ink xl:block">Panier</span>
          </Link>
        </nav>
      </div>

      <div className="relative bg-brand">
        <div className="container-it flex h-12 items-center gap-1">
          <button
            data-testid="mega-menu-trigger"
            onMouseEnter={() => setMegaOpen(true)}
            onClick={() => setMegaOpen(true)}
            className="-ml-2 flex h-full items-center gap-2 bg-brand-dark px-4 text-[13px] font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-[#0036B4]"
          >
            <Menu className="h-4 w-4" strokeWidth={2.6} />
            Toutes les catégories
            <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${megaOpen ? "rotate-180" : ""}`} />
          </button>

          <div ref={navContainerRef} className="relative flex min-w-0 flex-1 items-center gap-0.5">
            {items.slice(0, visibleCount).map((item) => renderNavItem(item))}

            {visibleCount < items.length && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-more-trigger"
                    className="flex items-center gap-1 whitespace-nowrap rounded-lg px-2.5 py-2 text-[13px] font-bold text-white transition-colors duration-200 hover:bg-white/15"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                    Plus
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  {items.slice(visibleCount).map((item) =>
                    item.type === "group" ? (
                      <div key={item.key}>
                        <p className="px-2 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                          {item.label}
                        </p>
                        {item.children.map((c) => (
                          <DropdownMenuItem asChild key={c.key}>
                            <Link
                              to={c.to}
                              data-testid={`nav-more-${c.testid}`}
                              className="flex cursor-pointer items-center gap-2 pl-4 text-sm font-semibold"
                            >
                              {c.label}
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </div>
                    ) : (
                      <DropdownMenuItem asChild key={item.key}>
                        <Link
                          to={item.to}
                          data-testid={`nav-more-${item.testid}`}
                          className="flex cursor-pointer items-center gap-2 text-sm font-semibold"
                        >
                          {item.Icon && <item.Icon className="h-4 w-4 text-brand" />}
                          {item.label}
                          {item.hot && (
                            <span className="ml-auto rounded bg-rose-500 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-white">
                              Hot
                            </span>
                          )}
                        </Link>
                      </DropdownMenuItem>
                    )
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <div
              ref={navMeasureRef}
              aria-hidden
              className="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-0.5"
            >
              {items.map((item) => renderNavItem(item, true))}
            </div>
          </div>
        </div>

        {megaOpen && (
          <div
            data-testid="mega-menu-panel"
            onMouseLeave={() => setMegaOpen(false)}
            className="absolute left-0 right-0 top-full z-50 border-t border-line bg-white shadow-[0_30px_60px_-20px_rgba(0,0,0,0.3)]"
          >
            <div className="container-it grid grid-cols-4 gap-3 py-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/boutique?categorie=${cat.slug}`}
                  data-testid={`mega-category-${cat.slug}`}
                  onClick={() => setMegaOpen(false)}
                  className="group flex items-start gap-3 rounded-xl border border-transparent p-3 transition-colors duration-200 hover:border-line hover:bg-brand-soft"
                >
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${cat.color}14`, color: cat.color }}
                  >
                    <DynamicIcon name={cat.icon} className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-ink group-hover:text-brand">{cat.name}</span>
                    <span className="mt-0.5 block text-xs leading-snug text-slate-500">{cat.description}</span>
                  </span>
                </Link>
              ))}
              <Link
                to="/boutique"
                onClick={() => setMegaOpen(false)}
                data-testid="mega-all-products"
                className="flex items-center justify-center gap-2 rounded-xl bg-ink p-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-brand"
              >
                <LayoutGrid className="h-4 w-4" /> Tout le catalogue
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export default DesktopHeader;
